import React from "react";
import { Link } from "react-router-dom";
import { FiHeart, FiGlobe, FiMapPin, FiMessageCircle, FiShield } from "react-icons/fi";
import { GiPrayerBeads, GiTempleGate } from "react-icons/gi";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import PersonCard from "../components/PersonCard";
import PageHero from "../components/PageHero";
import { SAURABH, GAURI, SITE } from "../utils/siteData";

export default function About() {
  return (
    <div>
      <SEO
        title="About Us"
        description="Learn about Pandit Saurabh Shastri's Vedic pooja services and Gauri Shastri's astrology & palmistry expertise, serving Thane and Mumbai with devotion and accuracy."
        keywords="about pandit thane, about astrologer thane, saurabh shastri, gauri shastri"
        path="/about"
      />

      <PageHero
        eyebrow="About Us"
        title="Rooted in Tradition"
        subtitle="Two voices of tradition and wisdom, dedicated to your spiritual and astrological well-being."
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 space-y-24">
          <div>
            <SectionTitle eyebrow="Our Story" title="Rooted in Vedic Tradition, Devoted to You" subtitle={`${SITE.name} brings together authentic Vedic ritual practice and accurate astrological guidance under one trusted name in Thane.`} />
            <p className="max-w-3xl mx-auto text-center text-gray-700 leading-relaxed">
              Our journey began with a simple devotion - to preserve the sanctity of Hindu rituals and to make accurate
              astrological wisdom accessible to families navigating life's important decisions. Pandit Saurabh Shastri
              brings years of disciplined Vedic training to every pooja and sanskar he performs, while Gauri
              Shastri combines deep astrological study with practical, real-world guidance through horoscope reading
              and palmistry. Together, they have helped hundreds of families in Thane, Mumbai and beyond find peace,
              clarity and divine blessing.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl text-maroon-800 text-center mb-10">Pandit Saurabh Shastri</h2>
            <PersonCard person={SAURABH} role="Vedic Pandit & Pooja Specialist" ctaLink="/pooja-services" ctaLabel="View Pooja Services" badge="Primary Pooja Expert" />
            <p className="max-w-3xl mx-auto text-center text-gray-700 leading-relaxed mt-10">{SAURABH.about}</p>
          </div>

          <div>
            <h2 className="font-heading text-2xl text-maroon-800 text-center mb-10">Gauri Shastri</h2>
            <PersonCard person={GAURI} role="Astrologer & Palmist" ctaLink="/astrology-palmistry-services" ctaLabel="View Astrology Services" reverse badge="Astrology & Palmistry Expert" />
            <p className="max-w-3xl mx-auto text-center text-gray-700 leading-relaxed mt-10">{GAURI.about}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-saffron-50">
        <div className="max-w-5xl mx-auto px-4">
          <SectionTitle eyebrow="Our Mission" title="Bringing Sacred Tradition Into Modern Life" />
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: <GiTempleGate size={28} />, title: "Authenticity", desc: "Every ritual and reading follows traditional, time-tested Vedic methods." },
              { icon: <FiHeart size={26} />, title: "Trust", desc: "Building lasting relationships with families through honesty and care." },
              { icon: <FiGlobe size={26} />, title: "Accessibility", desc: "Bringing pooja services and astrology guidance to Thane, Mumbai and the world." },
            ].map((m) => (
              <div key={m.title} className="bg-white rounded-2xl p-7 text-center shadow-premium border border-gold-100 hover-lift">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shadow-inner">
                  {m.icon}
                </div>
                <h4 className="font-heading text-maroon-800 mb-2">{m.title}</h4>
                <p className="text-sm text-gray-600">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <SectionTitle eyebrow="Why Families Trust Us" title="Clear Guidance, Traditional Practice" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <GiPrayerBeads size={26} />, title: "Shastra-Based Vidhi", desc: "Rituals are performed with traditional mantra, sankalp and step-by-step explanation." },
              { icon: <FiMapPin size={24} />, title: "Thane & Mumbai", desc: "In-person pooja services across Thane, Mumbai and nearby areas." },
              { icon: <FiGlobe size={24} />, title: "Online Guidance", desc: "Astrology and palmistry consultations available for clients worldwide." },
              { icon: <FiMessageCircle size={24} />, title: "Languages", desc: "Comfortable guidance in Marathi, Hindi and English." },
              { icon: <FiShield size={24} />, title: "Transparent Booking", desc: "Timing, samagri and contribution are discussed clearly before confirmation." },
            ].map((item) => (
              <div key={item.title} className="bg-saffron-50 border border-gold-100 rounded-2xl p-6 shadow-premium hover-lift">
                <div className="w-12 h-12 rounded-xl bg-white text-saffron-600 flex items-center justify-center mb-4 shadow-sm">
                  {item.icon}
                </div>
                <h3 className="font-heading text-maroon-800 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white text-center">
        <h3 className="font-heading text-2xl text-maroon-800 mb-4">Have Questions? We're Here to Help</h3>
        <Link to="/contact" className="btn-gold inline-block px-7 py-3 rounded-full font-semibold">
          Get in Touch
        </Link>
      </section>
    </div>
  );
}
